import { StatePanel } from "@/ds/custom/StatePanel";
import { Button } from "@/ds/shadcn/components/ui/button";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import { usePreferredPriceSource } from "@/features/common/api/usePreferredPriceSource";
import { DataView } from "@/features/common/components/dataview/DataView";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import type { GetProductAndVariantsMarketPriceResponseModel, VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { ProductView } from "@/features/products/components/ProductView";
import type { PriceAlertDetailsModel } from "@/features/priceAlerts/api/models/PriceAlertDetailsModel";
import { usePriceAlertDelete } from "@/features/priceAlerts/api/usePriceAlertDelete";
import { usePriceAlertDetails } from "@/features/priceAlerts/api/usePriceAlertDetails";
import { PriceAlertEdit } from "@/features/priceAlerts/components/PriceAlertEdit";
import { PagePriceAlertsDataViewGalleryItem } from "@/features/priceAlerts/components/PriceAlertGalleryItem";
import { PagePriceAlertsDataViewListItem } from "@/features/priceAlerts/components/PriceAlertListItem";
import { PriceAlertsTargetSelector } from "@/features/priceAlerts/components/PriceAlertTargetSelector";
import type { SelectedPriceAlertTarget } from "@/features/priceAlerts/SelectedPriceAlertTarget";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { useTitle } from "@/lib/common/useTitle";
import { MegaphoneIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";

export const PagePriceAlerts: FC = () => {
	useTitle("Price Alerts");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Price Alerts" }
	], []);

	return (
		<>
			<HeaderBar title="Price Alerts" description="Manage your price alerts to get notified about specific price changes" breadcrumb={breadcrumb} />

			<SubscriptionGate featureName="Price Alerts">
				<PagePriceAlertsInternal />
			</SubscriptionGate>
		</>
	);
};

type PriceAlertsRightPanel = "Add" | "Edit" | "View";

const PagePriceAlertsInternal: FC = () => {
	const priceAlertDetails = usePriceAlertDetails();
	const preferredCurrency = usePreferredCurrency();
	const preferredPriceSource = usePreferredPriceSource();
	const currency = useMemo(() => preferredCurrency.data?.currency ?? "USD", [preferredCurrency.data?.currency]);
	const priceSource = useMemo(() => preferredPriceSource.data?.priceSource ?? "TcgPlayerMarket", [preferredPriceSource.data?.priceSource]);

	const [priceAlertRightPanel, setPriceAlertRightPanel] = useState<PriceAlertsRightPanel | null>(null);
	const [selectedPriceAlertTarget, setSelectedPriceAlertTarget] = useState<SelectedPriceAlertTarget | null>(null);
	const [editingPriceAlert, setEditingPriceAlert] = useState<PriceAlertDetailsModel | null>(null);
	const [viewingProductId, setViewingProductId] = useState<string | null>(null);
	const [priceAlertIdToDelete, setPriceAlertIdToDelete] = useState<string | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const rightPanelScrollRef = useRef<HTMLDivElement | null>(null);

	const deletePriceAlert = usePriceAlertDelete();
	useNotifyMutationSuccess(deletePriceAlert, "Price Alert Deleted", "Your price alert was deleted successfully.");

	useEffect(() => {
		if (!deletePriceAlert.isSuccess) {
			return;
		}

		setPriceAlertIdToDelete(null);
	}, [deletePriceAlert.isSuccess]);

	const onAddPriceAlert = useCallback(() => {
		setSelectedPriceAlertTarget(null);
		setEditingPriceAlert(null);
		setPriceAlertRightPanel("Add");
	}, []);

	const onSelectVariant = useCallback((product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => {
		const target: SelectedPriceAlertTarget = { targetType: "Variant", product, variant };
		setSelectedPriceAlertTarget(target);
		setPriceAlertRightPanel("Edit");
	}, []);

	const onSelectCollection = useCallback((collection: CollectionModel, targetType: "Collection" | "CollectionVariants") => {
		const target: SelectedPriceAlertTarget = { targetType, collection };
		setSelectedPriceAlertTarget(target);
		setPriceAlertRightPanel("Edit");
	}, []);

	const onViewPriceAlert = useCallback((targetLinkId: string) => {
		setViewingProductId(targetLinkId);
		setPriceAlertRightPanel("View");
	}, []);

	const onEditPriceAlert = useCallback((priceAlert: PriceAlertDetailsModel) => {
		setSelectedPriceAlertTarget(null);
		setEditingPriceAlert(priceAlert);
		setPriceAlertRightPanel("Edit");
	}, []);

	return (
		<>
			<HeaderBarTools>
				<Button variant="outline" className="w-full @md:w-32 cursor-pointer" onClick={onAddPriceAlert} disabled={priceAlertRightPanel === "Add"}>
					Add Price Alert
				</Button>
			</HeaderBarTools>

			<SplitScreen fixed visible={!!priceAlertRightPanel} onVisibleChange={visible => !visible && setPriceAlertRightPanel(null)}>
				<SplitScreenLeftPanel ref={scrollRef}>
					<DataView
						forQuery={priceAlertDetails}
						scrollRef={scrollRef}
						getKey={x => x.priceAlertId}
						listViewHeaderLabels={["Target", "Type", "Metric", "Status", ""]}
						listViewHeaderClassNames={["min-w-0", "w-40 @2xl:w-52", "w-32 @2xl:w-40", "w-28 @2xl:w-36", "w-16"]}
						listComponent={PagePriceAlertsDataViewListItem}
						galleryComponent={PagePriceAlertsDataViewGalleryItem}
						galleryMinWidth={208}
						additionalProps={{
							onDeletePriceAlert: setPriceAlertIdToDelete,
							onEditPriceAlert,
							onViewPriceAlert
						}}>

						<StatePanel
							icon={MegaphoneIcon}
							title="Price Alerts"
							description="Price alerts notify you when a product, or a collection's value, crosses a target threshold.">
							<Button
								variant="outline"
								disabled={priceAlertRightPanel === "Add"}
								onClick={onAddPriceAlert}>
								Add Price Alert
							</Button>
						</StatePanel>
					</DataView>
				</SplitScreenLeftPanel>

				<SplitScreenRightPanel ref={rightPanelScrollRef}>
					{
						priceAlertRightPanel === "Add" &&
						<PriceAlertsTargetSelector
							onSelectProduct={onSelectVariant}
							onSelectCollection={onSelectCollection}
							scrollRef={rightPanelScrollRef}
							onCancel={() => setPriceAlertRightPanel(current => current === "Edit" ? current : null)} />
					}

					{
						priceAlertRightPanel === "Edit" &&
						<PriceAlertEdit
							key={editingPriceAlert?.priceAlertId}
							target={selectedPriceAlertTarget}
							priceAlert={editingPriceAlert}
							currency={currency}
							priceSource={priceSource}
							onChangeTarget={() => setPriceAlertRightPanel("Add")}
							onClose={() => setPriceAlertRightPanel(null)} />
					}

					{
						priceAlertRightPanel === "View" && viewingProductId &&
						<ProductView productId={viewingProductId} />
					}
				</SplitScreenRightPanel>
			</SplitScreen>

			<ModalConfirm
				title="Delete Price Alert"
				description="Are you sure you want to delete this price alert?"
				confirmText="Delete"
				confirmDestructive
				isLoading={deletePriceAlert.isPending}
				error={deletePriceAlert.error}
				open={!!priceAlertIdToDelete}
				onOpenChange={open => {
					if (!open) {
						setPriceAlertIdToDelete(null);
						deletePriceAlert.reset();
					}
				}}
				onConfirm={() => priceAlertIdToDelete && deletePriceAlert.mutate(priceAlertIdToDelete)} />
		</>
	);
};
